import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Rating,
  Alert,
  Chip,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon,
  Language as LanguageIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getSupabaseClient } from '../../services/supabase';

interface ContentUpdateModalProps {
  open: boolean;
  onClose: () => void;
  urlId: string;
  urlPath: string;
  currentPosition?: number;
  onUpdateAdded: () => void;
}

const updateTypes = [
  { value: 'major_rewrite', label: '📝 Major Rewrite', color: '#9c27b0' },
  { value: 'content_expansion', label: '📚 Content Expansion', color: '#2196f3' },
  { value: 'technical_seo', label: '⚙️ Technical SEO', color: '#ff5722' },
  { value: 'schema_update', label: '🏗️ Schema Update', color: '#ff9800' },
  { value: 'ui_refresh', label: '🎨 UI Refresh', color: '#4caf50' },
  { value: 'custom', label: '🔧 Custom Update', color: '#607d8b' }
];

// User email to name and icon mapping
const USER_INFO: Record<string, { name: string; icon: string }> = {
  'newyorknederland@gmail.com': { name: 'Charlotte', icon: '🌷' }, // Dutch tulip
  'newyorkcitycontent@gmail.com': { name: 'Magali', icon: '✨' }, // Sparkle for content magic
  'langace@gmail.com': { name: 'Eric', icon: '🚀' }, // Rocket for speed
  'newyorkcitypublicrelations@gmail.com': { name: 'Danielle', icon: '📢' }, // PR megaphone
  'finlandnewyork@gmail.com': { name: 'Jaana', icon: '🌲' }, // Finnish forest
  'james@codeops.nl': { name: 'James', icon: '👨‍💻' }, // Developer
  'julienewyorkcity@gmail.com': { name: 'Margaux', icon: '🎭' }, // Theater masks
};

// Get user display info from email
const getUserInfo = (email: string | null): { name: string; icon: string } => {
  if (!email) return { name: 'Unknown User', icon: '👤' };
  return USER_INFO[email] || { name: email.split('@')[0], icon: '👤' };
};

// Country configurations
const COUNTRIES = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'COM', name: 'USA/Latin', flag: '🇺🇸' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'CN', name: 'China', flag: '🇨🇳' }
];

export const ContentUpdateModal: React.FC<ContentUpdateModalProps> = ({
  open,
  onClose,
  urlId,
  urlPath,
  currentPosition,
  onUpdateAdded
}) => {
  const [updateDate, setUpdateDate] = useState(new Date().toISOString().split('T')[0]);
  const [updateType, setUpdateType] = useState<string>('content_expansion');
  const [impactScore, setImpactScore] = useState<number>(3);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [applyToAllWebsites, setApplyToAllWebsites] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset form when opening
      setUpdateDate(new Date().toISOString().split('T')[0]);
      setUpdateType('content_expansion');
      setImpactScore(3);
      setNotes('');
      setError('');
      setApplyToAllWebsites(false);
      setSelectedCountries([]);
      setShowCountrySelector(false);
      
      // Extract current country from URL ID
      const currentCountry = urlId.split('-')[0];
      if (currentCountry) {
        setSelectedCountries([currentCountry]);
      }
      
      // Get current user
      (async () => {
        const supabase = await getSupabaseClient(true);
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUserEmail(user?.email || null);
      })();
    }
  }, [open, urlId]);

  const handleSave = async () => {
    if (!notes.trim()) {
      setError('Please add notes about what was updated');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const supabase = await getSupabaseClient(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || null;
      
      // Extract the content ID (everything after the first hyphen)
      const contentId = urlId.substring(urlId.indexOf('-'));
      
      // Determine which countries to update
      let countriesToUpdate = [urlId.split('-')[0]]; // Default to current country
      
      if (applyToAllWebsites) {
        if (selectedCountries.length > 0) {
          countriesToUpdate = selectedCountries;
        } else {
          // Update ALL countries
          countriesToUpdate = COUNTRIES.map(c => c.code);
        }
      }
      
      // Create update records for each selected country
      const updates = countriesToUpdate.map(countryCode => {
        const targetUrlId = `${countryCode}${contentId}`;
        return {
          url_id: targetUrlId,
          update_date: updateDate,
          update_type: updateType,
          impact_score: impactScore,
          notes: notes.trim(),
          created_by: userEmail,
          position_before: countryCode === urlId.split('-')[0] ? (currentPosition || null) : null
        };
      });

      // Cast to any to bypass TypeScript checking for new table
      const { error: insertError } = await (supabase as any)
        .from('content_updates')
        .insert(updates);

      if (insertError) throw insertError;

      onUpdateAdded();
      onClose();
    } catch (err) {
      console.error('Error saving content update:', err);
      setError('Failed to save update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getUpdateTypeConfig = (type: string) => {
    return updateTypes.find(t => t.value === type) || updateTypes[0];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6">Log Content Update</Typography>
          </Box>
          <Button onClick={onClose} size="small" sx={{ minWidth: 'auto' }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* URL Display */}
          <Alert severity="info" icon={false}>
            <Typography variant="caption" color="text.secondary">
              Recording update for:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {urlPath}
            </Typography>
          </Alert>

          {/* Date Picker */}
          <TextField
            fullWidth
            label="Update Date"
            type="date"
            value={updateDate}
            onChange={(e) => setUpdateDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          {/* Update Type */}
          <FormControl fullWidth>
            <InputLabel>Update Type</InputLabel>
            <Select
              value={updateType}
              onChange={(e) => setUpdateType(e.target.value)}
              label="Update Type"
            >
              {updateTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{type.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              What kind of update did you make?
            </FormHelperText>
          </FormControl>

          {/* Impact Score */}
          <Box>
            <Typography component="legend" gutterBottom>
              Impact Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={impactScore}
                onChange={(_, value) => value && setImpactScore(value)}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#7b61ff'
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {impactScore === 1 && 'Minor tweaks'}
                {impactScore === 2 && 'Small improvements'}
                {impactScore === 3 && 'Moderate changes'}
                {impactScore === 4 && 'Significant update'}
                {impactScore === 5 && 'Complete overhaul'}
              </Typography>
            </Box>
          </Box>


          {/* Notes */}
          <TextField
            fullWidth
            label="Update Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe what was updated... e.g., Added FAQ section, rewrote introduction, optimized images, etc."
            error={!!error && !notes.trim()}
            helperText={error && !notes.trim() ? error : 'Be specific - this helps track what changes actually improve rankings!'}
            InputProps={{
              startAdornment: <NotesIcon sx={{ mr: 1, mt: 1, color: 'text.secondary', alignSelf: 'flex-start' }} />
            }}
          />

          {/* Multi-website Update Option */}
          <Box sx={{ 
            p: 2, 
            borderRadius: 1, 
            bgcolor: 'rgba(123, 97, 255, 0.05)',
            border: '1px solid',
            borderColor: 'rgba(123, 97, 255, 0.2)'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToAllWebsites}
                  onChange={(e) => {
                    setApplyToAllWebsites(e.target.checked);
                    if (e.target.checked) {
                      setShowCountrySelector(true);
                      // Pre-select all countries
                      setSelectedCountries(COUNTRIES.map(c => c.code));
                    } else {
                      setShowCountrySelector(false);
                      // Reset to current country only
                      const currentCountry = urlId.split('-')[0];
                      setSelectedCountries([currentCountry]);
                    }
                  }}
                  sx={{ color: '#7b61ff' }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body2">
                    Apply this update to all website versions
                  </Typography>
                </Box>
              }
            />
            
            <Collapse in={showCountrySelector}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Select websites to update (or leave all selected):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {COUNTRIES.map(country => (
                    <Chip
                      key={country.code}
                      label={`${country.flag} ${country.code}`}
                      onClick={() => {
                        setSelectedCountries(prev => 
                          prev.includes(country.code)
                            ? prev.filter(c => c !== country.code)
                            : [...prev, country.code]
                        );
                      }}
                      color={selectedCountries.includes(country.code) ? 'primary' : 'default'}
                      variant={selectedCountries.includes(country.code) ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={() => setSelectedCountries(COUNTRIES.map(c => c.code))}>
                    Select All
                  </Button>
                  <Button size="small" onClick={() => setSelectedCountries([])}>
                    Clear All
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Selected Update Type Preview */}
          <Box sx={{ 
            p: 2, 
            borderRadius: 1, 
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Chip
              label={getUpdateTypeConfig(updateType).label}
              sx={{
                bgcolor: getUpdateTypeConfig(updateType).color,
                color: 'white',
                fontWeight: 500
              }}
            />
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {currentPosition && (
                <>Current position: #{currentPosition} • </>
              )}
              {applyToAllWebsites && (
                <>Updating {selectedCountries.length} websites • </>
              )}
              We'll track how rankings change after this update
            </Typography>
            {currentUserEmail && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                Logged by: {getUserInfo(currentUserEmail).icon} {getUserInfo(currentUserEmail).name}
              </Typography>
            )}
          </Box>

          {error && notes.trim() && (
            <Alert severity="error">{error}</Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !notes.trim()}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <TrendingUpIcon />}
          sx={{
            bgcolor: '#7b61ff',
            color: 'white',
            '&:hover': {
              bgcolor: '#6c5ce7',
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666'
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};